import React from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { observer } from "mobx-react";
import { Form, Input, TextArea, Select } from "semantic-ui-react";
import FormRadioGroup from "~/components/element/FormRadioGroup";
import MediaItemClassName from "~/constants/Media/MediaItemClassName";

/**
 * A simple/generic form for media. Only contains the form and fields.
 * Do not use this standalone, but wrapped via ExpandedForm and InlineForm.
 */
const GenericForm = observer(
  ({ model, validation, serverValidation, bindInput, setProperty, t, onSubmit, children }) => (
    <Form
      onSubmit={event => {
        event.preventDefault();
        onSubmit();
      }}
    >
      <Form.Field
        label={t("clientPanelInfo:name")}
        control={Input}
        {...bindInput("name", validation, serverValidation)}
      />
      <Form.Field
        control={TextArea}
        label={t("clientPanelInfo:description")}
        {...bindInput("description", validation, serverValidation)}
      />
      {model.className === MediaItemClassName.PHOTO ? (
        <FormRadioGroup
          title={t("clientPanelMisc:mediaType")}
          name="isNavigationLayer"
          onChange={(property, value) => setProperty(property, Boolean(value))}
          options={[
            { id: "photo", label: t("photo"), value: "", checked: !model.isNavigationLayer },
            { id: "floorplan", label: t("floorPlan"), value: 1, checked: model.isNavigationLayer },
          ]}
        />
      ) : null}

      <FormRadioGroup
        title={t("clientPanelMisc:includeIn")}
        name="includeIn"
        onChange={setProperty}
        options={[
          {
            id: "both",
            label: t("clientPanelMisc:bothVirtualTourGallery"),
            value: "both",
            checked: model.includeIn === "both",
          },
          {
            id: "gallery",
            label: t("clientPanelMisc:galleryOnlyFitZephyr"),
            value: "gallery",
            checked: model.includeIn === "gallery",
          },
          {
            id: "neither",
            label: t("clientPanelMisc:neitherVirtualTourNorGallery"),
            value: "neither",
            checked: model.includeIn === "neither",
          },
        ]}
      />
      {model.className !== MediaItemClassName.VIDEO_CLIP ? (
        <>
          <Form.Field
            control={Input}
            label={t("clientPanelMisc:imageDisplayTimeDefault", { defaultDuration: model.displayDuration })}
            {...bindInput("duration", validation, serverValidation)}
          />
          {model.className === MediaItemClassName.VIRTUAL_TOUR && (
            <Form.Field className="help">
              {t("clientPanelMisc:virtualTourDurationHelp", { defaultDuration: model.displayDuration })}
            </Form.Field>
          )}
        </>
      ) : null}
      {model.className === MediaItemClassName.VIRTUAL_TOUR && (
        <FormRadioGroup
          title={t("clientPanelMisc:panoramaDirection")}
          name="panRight"
          onChange={(prop, value) => setProperty(prop, Boolean(value))}
          options={[
            {
              id: "right",
              label: t("clientPanelMisc:panRight"),
              value: 1,
              checked: model.panRight,
            },
            {
              id: "left",
              label: t("clientPanelMisc:panLeft"),
              value: "",
              checked: !model.panRight,
            },
          ]}
        />
      )}
      <Form.Field>
        <label>{t("clientPanelMisc:displayIn")}</label>
        <Select
          value={model.brandingMode}
          onChange={(_e, { value }) => setProperty("brandingMode", value)}
          options={[
            { key: "0", value: 0, text: t("bothBrandedUnbranded") },
            { key: "1", value: 1, text: t("brandedOnly") },
            { key: "2", value: 2, text: t("unbrandedOnly") },
          ]}
        />
      </Form.Field>
      {children}
    </Form>
  ),
);

GenericForm.propTypes = {
  model: PropTypes.shape({
    className: PropTypes.oneOf(["Photo", "VideoClip", "VirtualTour"]).isRequired,
    imagePathWebLarge: PropTypes.string.isRequired,
    name: PropTypes.string,
    description: PropTypes.string,
    includeIn: PropTypes.string,
    duration: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    displayDuration: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    brandingMode: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    isNavigationLayer: PropTypes.bool,
    panRight: PropTypes.bool,
  }),
  // the return value of ValidateSchema.getValidationErrors()
  validation: PropTypes.shape({
    isValid: PropTypes.bool,
    fields: PropTypes.shape({
      name: PropTypes.shape({
        isValid: PropTypes.bool,
        errors: PropTypes.arrayOf(PropTypes.string),
      }),
      description: PropTypes.shape({
        isValid: PropTypes.bool,
        errors: PropTypes.arrayOf(PropTypes.string),
      }),
      duration: PropTypes.shape({
        isValid: PropTypes.bool,
        errors: PropTypes.arrayOf(PropTypes.string),
      }),
    }).isRequired,
  }).isRequired,
  serverValidation: PropTypes.shape({
    name: PropTypes.string,
    description: PropTypes.string,
    duration: PropTypes.string,
  }).isRequired,
  setProperty: PropTypes.func.isRequired,
  bindInput: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  children: PropTypes.node,
};

export default withTranslation()(GenericForm);
